// Adapters: live Analytics API (camelCase) → the dashboard's snake_case
// contract in `src/types/analytics.ts`. Mirrors fleet-adapters.ts: the
// camel↔snake mismatch is absorbed in exactly one place.

import type {
  AnalyticsOverview,
  ConnectedUsers,
  PopularRegion,
  PopularRegions,
  UserGeography,
} from '@/types/analytics';

// ─── Live API response shapes (camelCase, as the backend serves) ───

export interface ApiAnalyticsOverview {
  connectedUsersNow: number;
  totalUsers: number;
  activeNodes: number;
  totalNodes: number;
  regionsCovered: number;
  generatedAt: string;
}

export interface ApiConnectedUsers {
  totalConnectedUsers: number;
  breakdown: {
    vpnSessions: number;
    proxySessions: number;
  };
  /** Optional — the backend may omit a time series. */
  series?: number[];
}

export interface ApiPopularRegion {
  countryCode: string | null;
  continent: string | null;
  activeSessions: number;
}

/**
 * GET /analytics/regions/popular. The API answers `{ regions, degraded }` since
 * KB #854; before that it answered a bare array. The SPA (Cloudflare Pages) and
 * the API image deploy separately, so both shapes are read.
 */
export type ApiPopularRegionsResponse =
  | ApiPopularRegion[]
  | { regions: ApiPopularRegion[]; degraded?: boolean };

export interface ApiUserGeographyEntry {
  countryCode: string;
  countryName: string;
  userCount: number;
}

export interface ApiUserGeography {
  countries: ApiUserGeographyEntry[];
  unknownCount: number;
}

// ─── Adapters ───────────────────────────────────────────────────────────────

/** GET /analytics/overview → AnalyticsOverview. */
export function adaptOverview(api: ApiAnalyticsOverview): AnalyticsOverview {
  return {
    connected_users: api.connectedUsersNow,
    total_users: api.totalUsers,
    active_nodes: api.activeNodes,
    total_nodes: api.totalNodes,
    regions_covered: api.regionsCovered,
    generated_at: api.generatedAt,
  };
}

/** GET /analytics/connected-users → ConnectedUsers. */
export function adaptConnectedUsers(api: ApiConnectedUsers): ConnectedUsers {
  return {
    total: api.totalConnectedUsers,
    breakdown: {
      vpn_sessions: api.breakdown.vpnSessions,
      proxy_sessions: api.breakdown.proxySessions,
    },
    series: api.series,
  };
}

function adaptPopularRegion(r: ApiPopularRegion): PopularRegion {
  return {
    country_code: r.countryCode,
    continent: r.continent,
    active_sessions: r.activeSessions,
  };
}

/**
 * GET /analytics/regions/popular → PopularRegions. The legacy bare array reads
 * as not degraded: that API had no way to say its query failed.
 */
export function adaptPopularRegions(api: ApiPopularRegionsResponse): PopularRegions {
  if (Array.isArray(api)) {
    return { regions: api.map(adaptPopularRegion), degraded: false };
  }

  return {
    regions: (api?.regions ?? []).map(adaptPopularRegion),
    degraded: api?.degraded === true,
  };
}

/** GET /analytics/users/geography → UserGeography. */
export function adaptUserGeography(api: ApiUserGeography): UserGeography {
  return {
    countries: (api.countries ?? []).map((c) => ({
      country_code: c.countryCode,
      country_name: c.countryName,
      users: c.userCount,
    })),
    unknown_count: api.unknownCount,
  };
}
